import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import type { WithOptionalProps } from "@b2bportal/core-types";
import type { DateRange, GuestsSelection } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  ensureFunnel,
  HOTEL_URL_PARAM_KEYS,
  HotelNavigationUtility,
  maybeStartFunnel,
} from "@overrides/utilities";
import dayjs from "dayjs";
import { HotelsShopSelectors, initialShopGuests } from "../../store";

export const useHotelShopSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useI18nContext();

  const shopSearch = useSelector(HotelsShopSelectors.getShopSearch);
  const [formDateRange, setFormDateRange] = useState<
    WithOptionalProps<DateRange, "from" | "until">
  >({});

  const { dateRange, guests, rooms } = useMemo(() => {
    return shopSearch != null
      ? shopSearch
      : { dateRange: undefined, guests: initialShopGuests, rooms: undefined };
  }, [shopSearch]);
  useEffect(() => {
    if (
      formDateRange.from == null &&
      formDateRange.until == null &&
      dateRange != null
    ) {
      setFormDateRange({ from: dateRange.from, until: dateRange.until });
    }
  }, [dateRange, formDateRange]);

  const handleDateRangeFromChange = (date: string | undefined) => {
    setFormDateRange({ from: date, until: undefined });
  };
  const handleDateRangeUntilChange = (date: string | undefined) => {
    setFormDateRange((prev) => ({ ...prev, until: date }));
  };

  const handleSearch = useCallback(
    (dateRange: DateRange, guests: GuestsSelection, rooms?: number) => {
      // TODO: navigation flow

      const searchParams = new URLSearchParams(location.search);

      const updatedParams = HotelNavigationUtility.buildSearchParams({
        dateRange: { from: dateRange.from, until: dateRange.until },
        guests,
        roomsCount: rooms,
      });

      // Delete updated keys first in order to append them later
      // Otherwise, the parameters of the same name such as childrenAges will be overwritten
      updatedParams.forEach((_, key) => {
        searchParams.delete(key);
      });
      updatedParams.forEach((value, key) => {
        searchParams.append(key, value);
      });

      // If the user deleted the children, `updatedParams` don't have the key
      // but the current query parameter still has it. Delete them.
      if (
        searchParams.has(HOTEL_URL_PARAM_KEYS.CHILDREN_COUNT) &&
        !updatedParams.has(HOTEL_URL_PARAM_KEYS.CHILDREN_COUNT)
      ) {
        searchParams.delete(HOTEL_URL_PARAM_KEYS.CHILDREN_COUNT);
      }
      if (
        searchParams.has(HOTEL_URL_PARAM_KEYS.CHILDREN_AGES) &&
        !updatedParams.has(HOTEL_URL_PARAM_KEYS.CHILDREN_AGES)
      ) {
        searchParams.delete(HOTEL_URL_PARAM_KEYS.CHILDREN_AGES);
      }

      // Rotate the funnel ID when the updated search differs materially from
      // the current one.
      maybeStartFunnel(
        "lodging",
        HotelNavigationUtility.getHotelShopFunnelSearch(location.search),
        HotelNavigationUtility.getHotelShopFunnelSearch(searchParams.toString())
      );

      navigate({
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      });
    },
    [location.pathname, location.search, navigate]
  );

  // Fallback seeding for shop pages that do not fetch through
  // `useFetchHotelShop` (which seeds before issuing the shop request, so offers
  // carry the funnel ID). A no-op whenever that hook already ran.
  useEffect(() => {
    ensureFunnel(
      "lodging",
      HotelNavigationUtility.getHotelShopFunnelSearch(location.search)
    );
  }, [location.search]);

  const handleDateRangeClose = useCallback(() => {
    const { from, until } = formDateRange;
    if (
      from != null &&
      until != null &&
      dateRange != null &&
      (!dayjs(from).isSame(dateRange.from, "date") ||
        !dayjs(until).isSame(dateRange.until, "date"))
    ) {
      handleSearch({ from, until }, guests, rooms);
    } else {
      if (dateRange != null) {
        setFormDateRange(dateRange);
      }
    }
  }, [formDateRange, dateRange, guests, rooms, handleSearch]);

  const onGuestSelection = useCallback(
    (guests: GuestsSelection, modalRooms?: number) => {
      if (dateRange != null) {
        handleSearch(dateRange, guests, modalRooms ?? rooms);
      }
    },
    [dateRange, handleSearch, rooms]
  );

  return useMemo(
    () => ({
      context: {
        dateRange: formDateRange,
        locationLabel: t("core-hotels.search.location.label"),
        searchButtonLabel: t("core-hotels.search.button"),
        guests,
        rooms,
      },
      handlers: {
        handleDateRangeFromChange,
        handleDateRangeUntilChange,
        handleDateRangeClose,
        handleSearch,
        onGuestSelection,
      },
    }),
    [
      formDateRange,
      guests,
      handleDateRangeClose,
      handleSearch,
      onGuestSelection,
      rooms,
      t,
    ]
  );
};
